/**
 * Fires the push-permission prime once, at a high-intent moment: after the user
 * saves a World Cup pick. Shows a soft in-app pre-prompt first (so a "Not now"
 * doesn't burn the one-shot iOS OS prompt), then the OS dialog.
 *
 * Native + real (non-anonymous) users only — anonymous users get the sign-up
 * nudge instead, and web has no OS push.
 *
 * Coordinated with the WC→Arena onboarding snackbar (PostFirstPickPromptManager):
 * brand-new users see onboarding on their FIRST pick, so the push prime defers to
 * their SECOND save to avoid stacking two interruptions. See pushPrimeGate.helper.
 */
import { useEffect, useRef } from 'react'
import { Platform } from 'react-native'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext'
import { useDialog } from '../../shared/hooks/useDialog'
import { useWcPickSavedEventStore } from '../../worldcup/stores/wcPickSavedEventStore'
import { hasSeenWcToArenaBridge } from '../../tutorial/helpers/walkthroughSeenStorage.helper'
import { pushPrimedStorageKey, PUSH_PRIME_DIALOG } from '../constants/pushRegistration.constants'
import { shouldAttemptPushPrime } from '../helpers/pushPrimeGate.helper'

/**
 * Module-level guard: userIds whose prime has already STARTED in this app session.
 * Added synchronously before any await so a fast host remount cannot slip a second
 * dialog through the read-then-write window on the AsyncStorage 'primed' flag.
 */
const primeStartedUserIds = new Set<string>()

/**
 * @param requestPushPermission from usePushRegistration — passed in so the
 *   silent-registration effect mounts only once (in the host), not per hook.
 */
export function usePushPermissionPrime(requestPushPermission: () => Promise<boolean>): void {
  const { isLoaded, isSignedIn, isAnonymous, user } = useSupabaseAuth()
  const { showDialog } = useDialog()
  const savedCount = useWcPickSavedEventStore((s) => s.savedCount)
  const handledRef = useRef(false)

  // Snapshot whether the user had already seen the WC→Arena onboarding bridge
  // BEFORE any pick this session. Reading it at mount (not inside the save effect)
  // keeps the gate race-free against the onboarding manager, which flips the flag
  // while showing its snackbar. null = not yet resolved (treated as brand-new).
  const bridgeSeenAtMountRef = useRef<boolean | null>(null)
  useEffect(() => {
    const userId = user?.id
    if (Platform.OS === 'web' || !isLoaded || !isSignedIn || isAnonymous || !userId) return
    let cancelled = false
    void hasSeenWcToArenaBridge(userId).then((seen) => {
      if (!cancelled) bridgeSeenAtMountRef.current = seen
    })
    return () => {
      cancelled = true
    }
  }, [isLoaded, isSignedIn, isAnonymous, user?.id])

  useEffect(() => {
    if (Platform.OS === 'web') return
    if (!isLoaded || !isSignedIn || isAnonymous) return
    if (handledRef.current) return
    const userId = user?.id
    if (!userId) return
    if (primeStartedUserIds.has(userId)) return
    // Defer brand-new users past the onboarding snackbar (their first pick).
    if (!shouldAttemptPushPrime({ savedCount, bridgeSeenAtMount: bridgeSeenAtMountRef.current })) {
      return
    }

    // Claim the prime synchronously — before any await — so concurrent remounts
    // cannot both reach showDialog.
    handledRef.current = true
    primeStartedUserIds.add(userId)

    let cancelled = false
    void (async () => {
      const storageKey = pushPrimedStorageKey(userId)
      const alreadyPrimed = await AsyncStorage.getItem(storageKey)
      if (cancelled || alreadyPrimed === 'true') return
      // Mark primed up-front so a decline never re-prompts on later picks.
      await AsyncStorage.setItem(storageKey, 'true')

      const confirmed = await showDialog({
        title: PUSH_PRIME_DIALOG.TITLE,
        message: PUSH_PRIME_DIALOG.MESSAGE,
        confirmText: PUSH_PRIME_DIALOG.CONFIRM_TEXT,
        cancelText: PUSH_PRIME_DIALOG.CANCEL_TEXT,
      })
      if (cancelled || !confirmed) return
      await requestPushPermission()
    })()

    return () => {
      cancelled = true
    }
  }, [isLoaded, isSignedIn, isAnonymous, user?.id, savedCount, showDialog, requestPushPermission])
}
